import { Link } from "@remix-run/react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@core/api";
import { useCurrentUser } from "@core/hooks/useCurrentUser";
import type { PatientModel } from "@/apidtos";

/**
 * True when ListProviderPatients includes the synthetic in-office patient (Key prefix in-office-use).
 * Uses the same query key + staleTime as HeaderOrderingFor so the list is shared via React Query.
 */
export function useCanOrderInOffice(): boolean {
  const { user } = useCurrentUser();
  const api = useApi();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["header", "ordering-for", "patients"],
    queryFn: () => api.ListProviderPatients({ PageSize: 100 }),
    staleTime: 60_000,
    enabled: !!user,
  });
  if (!user) {
    return false;
  }
  if (isLoading || isError) {
    return false;
  }
  return (data?.Results ?? []).some((p: PatientModel) =>
    (p.Key?.startsWith("in-office-use") ?? false),
  );
}

/**
 * Renders the catalog link with ?loc=office only when the account may order for in-office.
 * Must be rendered under ClientOnly if used from a route that SSRs before user/query are ready.
 */
export function InOfficeOrderNavLink({ className, label, href }: { className?: string; label: string; href: string }) {
  const can = useCanOrderInOffice();
  if (!can) {
    return null;
  }
  return (
    <Link to={href} className={className} data-testid="nav-inoffice-order">
      {label}
    </Link>
  );
}
